import { type Currency, extractAmountFromMonetaryValue, type MonetaryValue } from '@luminovo/commons';
import { formatCurrency, type FormattingType } from '@luminovo/frontend-utils';

import { type ChartSeriesDef } from './types';

type AggregateName = 'sum' | 'max' | 'min' | 'avg' | 'count';

const aggregateFns: Record<AggregateName, (values: number[]) => number> = {
    sum: (values) => values.reduce((a, b) => a + b, 0),
    max: (values) => (values.length === 0 ? 0 : Math.max(...values)),
    min: (values) => (values.length === 0 ? 0 : Math.min(...values)),
    avg: (values) => (values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length),
    count: (values) => values.filter((v) => v !== 0).length,
};

type NumberConfig<TData, TSharedContext> = {
    key: string;
    label: string | (() => string);
    color: string;
    accessor: (row: TData, ctx: TSharedContext) => number | null | undefined;
    aggregate?: AggregateName;
    /**
     * Counts line items in a state (consigned, no offer, in stock) instead of measuring them: the accessor returns
     * 1 or 0, the rows are summed whatever `aggregate` says, and the count stays out of totals and the chart's unit.
     */
    status?: boolean;
    tooltip?: string | (() => string);
    formatValue?: (value: number) => string;
};

type MonetaryValueConfig<TData, TSharedContext> = {
    key: string;
    label: string | (() => string);
    color: string;
    accessor: (row: TData, ctx: TSharedContext) => MonetaryValue | null | undefined;
    aggregate?: Exclude<AggregateName, 'count'>;
    tooltip?: string | (() => string);
    formattingType?: FormattingType;
};

export function createChartColumnHelper<TData, TSharedContext = void>() {
    return {
        number(config: NumberConfig<TData, TSharedContext>): ChartSeriesDef<TData, TSharedContext> {
            const { key, label, color, accessor, aggregate = 'sum', status, tooltip, formatValue } = config;
            return {
                key,
                label,
                color,
                tooltip,
                dataType: 'number',
                extractValue: (row: TData, ctx: TSharedContext) => accessor(row, ctx) ?? 0,
                aggregateFn: aggregateFns[status ? 'sum' : aggregate],
                status,
                formatValue,
            };
        },

        monetaryValue(config: MonetaryValueConfig<TData, TSharedContext>): ChartSeriesDef<TData, TSharedContext> {
            const { key, label, color, accessor, aggregate = 'sum', tooltip, formattingType = 'default' } = config;

            return {
                key,
                label,
                color,
                tooltip,
                dataType: 'monetaryValue',
                extractValue: (row: TData, ctx: TSharedContext) => {
                    const mv = accessor(row, ctx);
                    return extractAmountFromMonetaryValue(mv);
                },
                aggregateFn: aggregateFns[aggregate],
                resolveFormatValue(ctx: TSharedContext, data: TData[]): ((value: number) => string) | undefined {
                    const ctxCurrency = (ctx as Record<string, unknown>)?.currency as Currency | undefined;
                    if (ctxCurrency) {
                        return (value: number) => formatCurrency(value, ctxCurrency, formattingType);
                    }

                    let resolvedCurrency: Currency | undefined;
                    for (const row of data) {
                        const mv = accessor(row, ctx);
                        if (!mv?.currency) continue;
                        if (resolvedCurrency && resolvedCurrency !== mv.currency) {
                            throw new Error(
                                `Mixed currencies detected in chart data: ${resolvedCurrency} vs ${mv.currency}. ` +
                                    `Provide a single currency via sharedContext.currency.`,
                            );
                        }
                        resolvedCurrency = mv.currency;
                    }

                    if (resolvedCurrency) {
                        const currency = resolvedCurrency;
                        return (value: number) => formatCurrency(value, currency, formattingType);
                    }

                    return undefined;
                },
            };
        },
    };
}
